import {ISSUE_TYPE_VACATION} from "../../../constants";
import {IssueType} from "../../../domain/entities/issue-type.entity";
import {belongsToRoot} from "../../../domain/helpers/issue-type-root.helper";

/**
 * Hardcoded German literal, deliberately NOT routed through lit-translate's
 * `get()`. This app has no i18n beyond German (only `de*.json` exist), and
 * the sibling auto-fill mechanism below (`resolveVacationAutoTitle`) was
 * always hardcoded too — no reason for this one to be the odd one out.
 *
 * Bugreport 2026-08-19/20 (Anke) — "Urlaub" ended up empty in the title
 * field despite being set: the FIRST attempted fix set it via `get(...)` on
 * `OpenNewVacationIssueDialogCommandHandler` — a command that turned out to
 * be dead code, never dispatched at runtime (the sidebar's "Urlaub" menu
 * item actually runs the INLINE `execute()` on `ActionIds.DIALOG_NEW_VACATION`
 * in `dialog.actions.ts`, via `actionRegistry` — a separate, duplicate
 * implementation of "create a new vacation issue" that this fix never
 * touched). Confirmed by instrumenting the real code path end-to-end
 * (console logging through `dialog.actions.ts` → `UiHelper.
 * openDialogEntityEdit` → `createNewEntity()` → `clone()` → the rendered
 * DOM), which is also why the command/handler pair was deleted afterward
 * rather than fixed a second time — one implementation, not two.
 */
export const VACATION_TITLE_PREFIX = 'Urlaub';

/**
 * The word an absence's title starts with (D55, Stefan 2026-10-20): the child's own name for a child
 * of the Absence root (e.g. "Krankheit"), "Urlaub" on the root itself; null outside the Absence family,
 * so the caller leaves an unrelated title alone.
 */
export function vacationTitlePrefix(type: IssueType | null | undefined): string | null {
    if (!belongsToRoot(type, ISSUE_TYPE_VACATION)) {
        return null;
    }
    return type!.parentUid ? type!.toString() : VACATION_TITLE_PREFIX;
}

/**
 * Computes the auto-filled title for an absence from its (single — `renderEmployeeAssignments` caps
 * assignments at 1 for this family) assigned employee's display name, e.g. "Urlaub Müller, Peter" or
 * "Krankheit Müller, Peter" (Anke, 2026-08-19: "als Titel immer automatisch voreingetragen werden:
 * 'Urlaub' + Mitarbeitername"; D55). Returns `null` when no auto-title should be applied — not an
 * absence, or no employee assigned (yet) — so the caller knows to leave the existing title untouched.
 */
export function resolveVacationAutoTitle(type: IssueType | null | undefined, employeeName: string | null | undefined): string | null {
    const prefix = vacationTitlePrefix(type);
    if (prefix === null || !employeeName) {
        return null;
    }

    return `${prefix} ${employeeName}`;
}

/**
 * Same auto-title as {@link resolveVacationAutoTitle}, for the assignment-CHANGE and type-CHANGE
 * subscriptions: removing the (sole) employee falls back to the bare prefix rather than leaving a
 * stale "<prefix> <old name>" behind (review finding 2026-08-20). Returns `null` only when the issue
 * isn't an absence at all, so the caller knows to leave an unrelated title alone.
 */
export function resolveVacationTitleForAssignmentChange(type: IssueType | null | undefined, employeeName: string | null | undefined): string | null {
    const prefix = vacationTitlePrefix(type);
    if (prefix === null) {
        return null;
    }

    return resolveVacationAutoTitle(type, employeeName) ?? prefix;
}
